import fileService, { buildDateFilter } from '@/modules/file/fileService'
import IconSearch from '@assets/images/icons/search.svg'
import IconPencil from '@assets/images/icons/pencil.svg'
import IconDownload from '@assets/images/icons/download.svg'
import IconDelete from '@assets/images/icons/trashcan.svg'
import IconDeselect from '@assets/images/icons/deselect.svg'
import IconFavorite from '@assets/images/icons/favorite.svg'
import IconNoFavorite from '@assets/images/icons/no_favorite.svg'
import IconTag from '@assets/images/icons/article.svg'
import IconTags from '@assets/images/icons/tags_solid.svg'
import IconDownloadZip from '@assets/images/icons/zip.svg'
import IconScanDocument from '@assets/images/icons/ocr.svg'
import IconCheckActive from '@assets/images/icons/checkbox_active.svg'
import IconMoveFolder from '@assets/images/icons/move_to_folder.svg'
import IconOpenFolder from '@assets/images/icons/folder_goto.svg'
import IconCheck from '@assets/images/icons/check_medium.svg'
import IconLoading from '@assets/images/logo/svg/logo-small.svg'
import orgamax from 'ampersand-app'
import store, { getAppState } from 'redux/store'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { getUploader } from '@/modules/file/uploader'
import { EXTERNAL_OWNER_SYSTEMS, FILE_LIST_TYPE, SORTABLE_FILE_FIELDS, TAG_LABELS, TAG_TYPES } from '@/modules/file/constants'
import { canWriteFolder, filterWritableFolderTree } from '@/modules/file/permissions'
import documentConfig from '@/modules/document/config'
// FLAG:useDocuAi — gate for the legacy „Analyse" / „Erneut analysieren" context-menu entry.
import { shouldShowLegacyDocumentScanAction } from '@/modules/document/utils/legacy-scan-action'

import { debounce, includes, pick, uniq } from 'lodash'

import lang from '@/lang'
import { SORT_ORDER } from '@/enums/pagination'
import { UNSAFE_openUpgradeModal as openUpgradeModal } from '@/hooks/useUpgradeModal'
import { FILE_LIST_FILTER, PERMISSION_TYPES, errorCodes, experimentalFeatures, mimeTypes, subscriptionLimits } from '@/modules/base/util/constants'
import DocumentType from '@/enums/document-type.enum'
import { selectColumn, getColumnProps } from '@/redux/ducks/document/config'
import { dispatch, getList, LIST_TYPES, QUERY_TYPES, setShallRefresh, updateQuickFilter } from '@/redux/ducks/lists'
import customDecodeUri from '@/utils/customDecodeUri'
import validateFileName from '@/utils/validateFileName'
import formatSizeLimitError from '@/modules/_helpers/formatSizeLimitError'
import {
  SHOW_LOADER_DOCUMENTS,
  HIDE_LOADER_DOCUMENTS,
  UPDATE_DOCUMENT_LIST_ITEM,
  UPDATE_DOCUMENT_LIST_ITEMS,
  SET_DOCUMENT_PAGINATION,
  SET_DOCUMENTS_TAGFILTER,
  SET_DOCUMENTS_FILTER,
  SET_DOCUMENT_SEARCH_TERM,
  SET_DOCUMENT_SORT_ORDER,
  REFRESH_DOCUMENTS,
  STOP_REFRESHING_DOCUMENTS,
  ERROR_FETCHING_DOCUMENT_LIST_ITEMS,
  ERROR_DELETING_DOCUMENT_LIST_ITEM,
  ERROR_ADDING_TAGS,
  ERROR_DOWNLOADING_ZIP,
  ERROR_UPDATING_COLUMNS,
  ERROR_FETCHING_COLUMNS,
  ERROR_SETTING_IS_FAVORITE,
  ERROR_HIDING_FILTERS,
  SET_LIST_DISPLAY_TYPE,
  REMOVE_DOCUMENTS_ITEMS,
  UPDATE_TAG_FROM_DOCUMENTS_ITEMS,
  REMOVE_TAG_FROM_DOCUMENTS_ITEMS,
  FETCH_COMPLETE,
  INIT_DELETION,
  CANCEL_DELETION,
  TAGS_FETCHED,
  ADD_TAG,
  REMOVE_TAG,
  SET_ITEM_SELECTION,
  REVOKE_SELECTION,
  SELECT_ALL,
  TOGGLE_SETTINGS_MODAL,
  LIST_SETTINGS_UPDATED,
  CLEAR_LIST,
  START_UPLOAD,
  ADD_FILE_TO_UPLOAD,
  FILE_UPLOADED,
  FOLDER_ADDED,
  EXECUTE_UPLOAD,
  UPLOAD_COMPLETE,
  SET_THUMBNAIL,
  PATCH_DOCUMENT_LIST_ITEM,
  PATCH_DOCUMENT_LIST_ITEM_META,
  DOCUMENT_SHARED,
  SET_FOLDER,
  SWITCH_COLLABORATION,
  UPDATE_COUNT,
  REMOVE_UPLOAD_FILE,
  OPEN_TAG_MODAL,
  CLOSE_TAG_MODAL,
  DOCUMENT_FOLDER_TREE,
  AG_GRID_UPDATED,
  FILTER_SETTINGS_UPDATED,
  SET_COLLABORATORS_DATA,
  CLOSE_UPLOAD_DRAWER,
  INSERT_DATA,
  REMOVE_UPLOAD_FROM_LIST,
  START_DOWNLOADING_ZIP,
  SUCCESS_DOWNLOADING_ZIP,
  SET_CLIENT_SUBSCRIPTION_EXPIRED,
} from './actions'
import buildFolders from './_helpers/buildFolders'
import { patchItemById, patchItemMetaById } from './_helpers/patchListItem'
import {
  downloadZip,
  fetchFolders,
  fetchListAction,
  fetchListSetting,
  fetchTags,
  setDocumentsFavorite,
} from '../lists/requests/documentList'
import isMSOfficeDocument from '@/modules/document/_helpers/isOfficeDocument'
import moment from 'moment'
import { Folder } from '@/modules/file/components/folder-nav/types'
import request from '@/modules/base/util/request'
import { navigate } from '@/modules/layout/util/useInterceptedNavigate'
import { openDocumentDrawer } from '@/redux/ducks/document/documentDrawerHelpers'

const { NOT_ALLOWED, EXISTS } = errorCodes
const { DOCUMENTS_LIST_TYPE } = LIST_TYPES

const onError = (id, name, errorReason, xhr) => {
  const { uploaderErrors } = store.getState().document.list

  if (xhr !== undefined) {
    if (xhr.status === 400) {
      uploaderErrors.push(name)
    }
    const response = JSON.parse(xhr.response)
    if (response.name === 'DmsError') {
      toast.showToast({ type: 'error', message: lang.dmsError })
    }
    if (response?.meta[subscriptionLimits.STORAGE_SPACE]) {
      openUpgradeModal({
        subscriptionChange: true,
        subscriptionType: 'document',
        limitReached: subscriptionLimits.STORAGE_SPACE,
      })
    } else if (
      response?.meta[subscriptionLimits.DOCUMENT_COUNT]
      || response?.meta[subscriptionLimits.DOCUMENT_COUNT_YEAR]
    ) {
      openUpgradeModal({
        subscriptionChange: true,
        subscriptionType: 'document',
        limitReached: subscriptionLimits.DOCUMENT_COUNT,
      })
    } else if (response?.meta[`permission-${PERMISSION_TYPES.CREATE}`]) {
      toast.showToast({ type: 'error', message: lang.documents.folder.permissions[PERMISSION_TYPES.CREATE] })
    } else {
      if (!includes(uploaderErrors, name)) {
        uploaderErrors.push(name)
        toast.showToast({ type: 'error', message: errorReason })
      }
    }
  } else {
    if (!includes(uploaderErrors, name)) {
      uploaderErrors.push(name)
      toast.showToast({ type: 'error', message: formatSizeLimitError(errorReason) })
    }
    store.dispatch({
      type: UPLOAD_COMPLETE,
    })
  }
}

const onAllComplete = (succeeded, failed = []) => {
  const { uploader, uploaderErrors, listDisplayType } = store.getState().document.list

  if (succeeded) {
    const { filter } = getList(DOCUMENTS_LIST_TYPE)
    if (filter !== 'all') {
      succeeded.forEach((fileId) => {
        const { name } = uploader.methods.getFile(fileId)
        toast.showToast({
          type: 'success',
          title: lang.documents.uploadSuccessTitle,
          message: lang.documents.uploadDocumentMessage(name),
        })
      })
    }
  }

  failed.forEach((fileId) => {
    const { name } = uploader.methods.getFile(fileId)
    if (!includes(uploaderErrors.map((e) => customDecodeUri(e)), name)) {
      uploaderErrors.push(name)
    }
  })

  if (listDisplayType === FILE_LIST_TYPE.thumbs) {
    dispatch(fetchListAction({ clear: true, queryType: QUERY_TYPES.ALL }))
  }
  if (listDisplayType === FILE_LIST_TYPE.list) {
    dispatch(setShallRefresh(LIST_TYPES.DOCUMENTS_LIST_TYPE))
    dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER }))
  }
  dispatch(fetchFolders())

  if (uploaderErrors.length) {
    toast.showToast({
      type: 'error',
      title: lang.documents.uploadSuccessTitle,
      message: lang.documents.multipleUploadErrors(uploaderErrors.length, uploaderErrors.map(customDecodeUri).join(', ')),
    })
  }

  store.dispatch({
    type: UPLOAD_COMPLETE,
  })
}

const onComplete = (id, _ /* unused */, response) => {
  const { uploader } = store.getState().document.list

  if (!response.success) return
  const { data } = response
  const [file] = data
  uploader.methods.setUuid(id, file.id)
  store.dispatch({
    type: FILE_UPLOADED,
    file,
  })
}

const onSubmitted = (id) => {
  const { uploader } = store.getState().document.list
  uploader.methods.setName(id, encodeURI(uploader.methods.getName(id)))

  if (window.location.href.includes('/dashboard') || window.location.pathname === '/') {
    navigate?.('/documents?uploadFromDashboard=true')
  }

  store.dispatch({
    type: ADD_FILE_TO_UPLOAD,
    file: uploader.methods.getFile(id),
  })
}

const onValidateBatch = (files) => {
  const {
    upload: { state },
  } = store.getState().document.list
  if (state === 'started') return false

  const validatedFiles = files.map(validateFileName).filter(Boolean)
  if (!validatedFiles.length) return false

  store.dispatch({
    type: START_UPLOAD,
    count: files.length,
  })

  return files
}

const createTagItem = (file, selectedItems, label, backgroundColor, id, isSelected) => {
  const filterItems = selectedItems.length && isSelected ? selectedItems : [file]
  const remove
    = filterItems?.filter(({ tags }) => tags?.find((tag) => tag?.label === label)).length === filterItems.length

  const onClick = remove
    ? () => store.dispatch(removeTags(label, filterItems))
    : () => store.dispatch(addTags(label, filterItems))

  return { id, label, backgroundColor, onClick, checked: remove, filterItems }
}

const addTagSelectionItem = (
  menuItems,
  selectedItems,
  selectedCount,
  isSelected,
  file,
  tagOptions,
  key,
  label,
  icon,
) => {
  if (!tagOptions.length) {
    return
  }
  const children = tagOptions.map(({ id, label, backgroundColor }) =>
    createTagItem(file, selectedItems, label, backgroundColor, id, isSelected),
  )

  const isInvoice = selectedItems.some((item) => item.isAssigned === true)
  const isDisabled = !!((file.isAssigned || isInvoice) && (key === 'kindAssignment' || key === 'selectionKindAssignment'))
  const tooltip = isDisabled ? lang.documents.disabledIconToolTip : label
  if (selectedItems.length || !selectedCount) {
    menuItems.push({ label, key, icon, disabled: isDisabled, children, tooltip })
  }
}

export const addMoveMenuItem = (
  menuItems,
  files,
  folders,
  { includesRoot = false, callback = null, allowSystemFolders, isSelection, disabled = false },
) => {
  const move = async(folderId) => {
    try {
      await dispatch(
        moveFiles(
          files.map(({ id }) => id),
          folderId,
        ),
      )
    } catch (error) {
      // Error is already handled by moveFiles action
    }
  }

  const makeMoveItem = (folder) => {
    const { name, id, children } = folder
    const validChildren = children.length
      ? children
        .filter(({ isSystemFolder }) => allowSystemFolders || !isSystemFolder)
        .map((child) => makeMoveItem(child))
      : []
    const onMove = () => (callback ? callback(id) : move(id))
    const isWritable = canWriteFolder(folder)

    return {
      label: name ?? lang.documents.folder.rootDirectory,
      children: validChildren.length ? validChildren : undefined,
      onClick: isWritable ? onMove : undefined,
    }
  }

  const writableFolders = filterWritableFolderTree(folders) || folders
  const children = includesRoot
    ? [makeMoveItem(writableFolders)]
    : writableFolders.children.length
      ? writableFolders.children
        .filter(({ isSystemFolder }) => allowSystemFolders || !isSystemFolder)
        .map((child) => makeMoveItem(child))
      : []

  if (children.length) {
    menuItems.push({
      label: lang.documents.moveMenuLabel,
      key: isSelection ? 'selectionMoveToFolder' : 'moveToFolder',
      icon: IconMoveFolder,
      children,
      disabled,
    })
  }
}

const openDocumentFolder = (file, folders) => {
  if (!file.metaData.path) {
    return
  }

  let folder = folders
  file.metaData.path.split('/').forEach((node) => {
    folder = folder.children.find(({ name }) => name === node)
  })
  setFolder(folder)
}

const getContextMenu = (rights, file) => {
  const state = store.getState()
  const { isOwner, isOwnerTaxConsultant } = state.user.info
  const { selectedItems, folders, currentFolder, isReadOnly } = state.document.list

  const selectedCount = selectedItems.length
  const isSelected = selectedItems.find(({ id }) => id === file.id)
  const isCollaborationView = !!state.document.list.currentCollaboration
  const isUserTaxConsultant = isOwnerTaxConsultant && !isOwner
  const isTaxAccountant = state.subscription.details.subscriptions.some((s) => s.isTaxAccountant)
  const isOfficeDocument = isMSOfficeDocument(file?.mimetype)
  const isCsv = file?.mimetype === mimeTypes.MIMETYPE_CSV

  const menuItems = []

  const kindOptionNames = [
    TAG_LABELS.NOT_ASSIGNED,
    TAG_LABELS.INCOMING_INVOICE,
    TAG_LABELS.INVOICE,
    TAG_LABELS.INVOICE_CORRECTION,
    TAG_LABELS.CONTRACT,
    TAG_LABELS.CANCELLATION,
  ].map((label) => ({ label }))

  const isDisabled = file.isAssigned || !rights.write || isCollaborationView || isUserTaxConsultant || file.externalOwnerSystem || file.signatureCount || isReadOnly
  if (selectedCount) {
    menuItems.push({
      label: lang.generics.selectAll,
      key: 'selectAll',
      icon: IconCheckActive,
      onClick: () => store.dispatch(selectAll()),
      tooltip: lang.documents.selectAllTooltip,
      showInContextMenu: false,
    })
  }

  if (selectedCount > 1) {
    menuItems.push({ key: 'selectionHeader', label: `${lang.generics.selection} (${selectedItems.length})` })
    menuItems.push({
      label: lang.generics.revokeSelection,
      key: 'revokeSelection',
      icon: IconDeselect,
      onClick: () => store.dispatch(revokeSelection()),
    })

    if (isSelected) {
      const isLoading = state.document.list.isDownloading
      menuItems.push({
        label: isLoading ? lang.generics.downloadComplete : lang.generics.downloadSelection,
        key: 'downloadZip',
        icon: isLoading ? IconLoading : IconDownloadZip,
        onClick: () => store.dispatch(downloadZip(selectedItems, isTaxAccountant)),
      })
    }

    const deletableItems = selectedItems.filter(({ isAssigned }) => isAssigned === false)
    const deletableCount = deletableItems.length
    if ((deletableCount >= 1 || isSelected) && !isCollaborationView && !isUserTaxConsultant) {
      menuItems.push({
        label: lang.generics.deleteSelection,
        key: 'removeBatch',
        icon: IconDelete,
        tooltip: isDisabled ? lang.documents.disabledIconToolTip : lang.generics.deleteSelection,
        disabled: isDisabled,
        onClick: () => store.dispatch(initDeletion(deletableItems)),
      })
    }

    if (isSelected && !isCollaborationView && !isUserTaxConsultant) {
      addTagSelectionItem(
        menuItems,
        selectedItems,
        selectedCount,
        isSelected,
        file,
        kindOptionNames,
        'selectionKindAssignment',
        lang.documents.assignKind,
        IconTag,
      )
      menuItems.push({
        label: lang.documents.assignTag,
        key: 'selectionTagAssignment',
        icon: IconTags,
        tooltip: lang.documents.assignTag,
        onClick: () =>
          store.dispatch(
            openTagModal(file, selectedItems, isSelected),
          ),
      })
    }

    addMoveMenuItem(menuItems, selectedItems, folders, { includesRoot: false, allowSystemFolders: true, isSelection: true, disabled: isReadOnly })

    if (!isCollaborationView && !isUserTaxConsultant) {
      if (selectedItems.some(({ isFavorite }) => isFavorite)) {
        menuItems.push({
          label: lang.documents.removeFavorite,
          key: 'selectionSetNoFavorite',
          icon: IconNoFavorite,
          onClick: () => store.dispatch(setDocumentsFavorite(selectedItems, false)),
        })
      } else {
        menuItems.push({
          label: lang.documents.setFavorite,
          key: 'selectionSetFavorite',
          icon: IconFavorite,
          onClick: () => store.dispatch(setDocumentsFavorite(selectedItems, true)),
        })
      }
    }
    if (currentFolder.unseenFilesCount) {
      const unseenFileIds = new Set(
        currentFolder.files
          .filter(file => file.isUnseen)
          .map(file => file.id),
      )
      const selectedUnseenFiles = selectedItems.filter(item =>
        unseenFileIds.has(item.id),
      )
      menuItems.push({ key: 'selectionMarkAsSeen', label: lang.generics.markAsSeen, icon: IconCheck, onClick: () => store.dispatch(markAsSeen(selectedUnseenFiles, currentFolder)) })
    }

  } else if (selectedCount < 2) {

    if (selectedCount && selectedCount === 1) {
      menuItems.push({
        label: lang.generics.revokeSelection,
        key: 'revokeSelection',
        icon: IconDeselect,
        onClick: () => store.dispatch(revokeSelection()),
      })
    }

    menuItems.push(
      { key: 'fileNameHeader', type: 'header', label: customDecodeUri(file.name) },
      { key: 'openDrawer', label: lang.generics.show, icon: IconSearch, onClick: () => openDocumentDrawer({ documentId: file.id, rowIndex: file.rowIndex }) },
    )

    const isDesktop = file.externalOwnerSystem === EXTERNAL_OWNER_SYSTEMS.desktop
    const isIncomingInvoice = file.mimetype === mimeTypes.MIMETYPE_PDF && file.tags?.find(({ tagType }) => tagType === TAG_TYPES.INCOMING_INVOICE)
    // FLAG:useDocuAi — this entry's twin in the drawer action menu is already gated
    // (document-actions-button.tsx: `if (!useDocuAiEnabled && !isAssigned && isIncomingInvoice
    // && !isDesktop)`), but the same action in the tile context menu was not. The rationale lives in
    // shouldShowLegacyDocumentScanAction; with flag-OFF the entry remains word-for-word unchanged.
    if (shouldShowLegacyDocumentScanAction({
      useDocuAiEnabled: orgamax.user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi),
      isCollaborationView,
      isUserTaxConsultant,
      isOfficeDocument,
      isCsv,
      isDesktop,
      isAssigned: file.isAssigned,
      isIncomingInvoice,
      documentType: file.type,
    })) {
      const item = { key: 'scan', onClick: () => openDocumentDrawer({ documentId: file.id, rowIndex: file.rowIndex, analyze: true }) }
      if (file.metaData?.ocr) {
        menuItems.push({ ...item, label: lang.documents.scanAgainButtonLabel, icon: IconScanDocument })
      } else {
        menuItems.push({ ...item, label: lang.documents.scanDocument, icon: IconScanDocument })
      }
    }

    menuItems.push({
      label: lang.generics.rename,
      key: 'rename',
      icon: IconPencil,
      // A desktop-owned file takes nothing but notes and tags (desktop-owned-file.ts), and the
      // drawer's name field is read-only for it anyway — the entry could only open a drawer in
      // which nothing can be renamed.
      disabled: [DocumentType.INVOICE, DocumentType.CANCELLATION].includes(file.type) || isDesktop,
      onClick: () => openDocumentDrawer({ documentId: file.id, rowIndex: file.rowIndex, rename: true }),
    })

    if (!isCollaborationView && !isUserTaxConsultant) {
      if (file.isFavorite) {
        menuItems.push({
          label: lang.documents.removeFavorite,
          key: 'setNoFavorite',
          icon: IconNoFavorite,
          onClick: () => store.dispatch(setDocumentsFavorite([file], false)),
        })
      } else {
        menuItems.push({
          label: lang.documents.setFavorite,
          key: 'setFavorite',
          icon: IconFavorite,
          onClick: () => store.dispatch(setDocumentsFavorite([file], true)),
        })
      }
    }
    if (file.unseen) {
      menuItems.push({ key: 'markAsSeen', label: lang.generics.markAsSeen, icon: IconCheck, onClick: () => dispatch(markAsSeen([file], file.metaData.path)) })
    }

    if (!file.uploadToken) {
      menuItems.push({
        label: lang.generics.download,
        key: 'download',
        icon: IconDownload,
        onClick: () =>
          window.open(
            `${file.url}?download=true&token=${orgamax.user.token}${isCollaborationView ? '&collaborationId=' + state.document.list.currentCollaboration : ''
            }`,
            '_blank',
          ),
      })
    }

    if ((selectedCount < 2 || !isSelected) && !isCollaborationView && !isUserTaxConsultant) {
      addTagSelectionItem(
        menuItems,
        selectedItems,
        selectedCount,
        isSelected,
        file,
        kindOptionNames,
        'kindAssignment',
        lang.documents.assignKind,
        IconTag,
      )
      menuItems.push({
        label: lang.documents.assignTag,
        key: 'tagAssignment',
        icon: IconTags,
        tooltip: lang.documents.assignTag,
        onClick: () =>
          store.dispatch(
            openTagModal(file, selectedItems, isSelected),
          ),
      })
    }

    if (currentFolder.isRoot) {
      menuItems.push({
        label: lang.documents.openDocumentFolder,
        key: 'openDocumentFolder',
        icon: IconOpenFolder,
        onClick: () => openDocumentFolder(file, folders),
      })
      menuItems.push({
        label: lang.documents.assignTag,
        key: 'tagAssignment',
        icon: IconTags,
        tooltip: lang.documents.assignTag,
        onClick: () =>
          store.dispatch(
            openTagModal(file, selectedItems, isSelected),
          ),
      })
    }

    addMoveMenuItem(menuItems, [file], folders, { includesRoot: false, allowSystemFolders: true, isSelection: selectedCount > 1, disabled: isReadOnly })

    if (!isCollaborationView && !isUserTaxConsultant) {
      menuItems.push({
        label: lang.generics.delete,
        key: 'removeSingle',
        icon: IconDelete,
        tooltip: isDisabled ? lang.documents.disabledIconToolTip : lang.generics.deleteSelection,
        disabled: isDisabled,
        onClick: () => store.dispatch(initDeletion([file])),
      })
    }
  }

  return menuItems
}

const uploader = getUploader({
  autoUpload: false,
  request: {
    requireSuccessJson: false,
  },
  validation: {
    stopOnFirstInvalidFile: false,
  },
  callbacks: {
    onError,
    onComplete,
    onAllComplete,
    onSubmitted,
    onValidateBatch,
  },
  params: {
    withTags: true,
  },
})

// reducer
const initialState = {
  iNav: -1,
  lastSelectedItem: null,
  isLoading: true,
  loadingText: 'fetching',
  error: null,
  items: [],
  options: {
    filter: 'all',
    tagFilter: {
      tags: [],
      date: [],
    },
    searchTerm: '',
    includes: [],
    excludes: [],
  },
  listDisplayType: FILE_LIST_TYPE.thumbs,
  pagination: {
    offset: 0, // current starting point in list
    count: 0, // count of filtered offers
    totalCount: 0, // count of all offers
    limit: 40, // max files on page
    sortOrder: SORT_ORDER.DESC,
    orderBy: SORTABLE_FILE_FIELDS.createdAt,
    path: null,
  },
  meta: { count: 0, totalCount: 0 },
  shouldRefresh: false,
  tagOptions: [],
  tagToAdd: null,
  tagToRemove: null,
  confirmDeleteOpen: false,
  tagModalOpen: false,
  filesToDelete: [],
  selectedItems: [],
  settingsModalOpen: false,
  tagAssignmentOpen: false,
  uploaderErrors: [],
  upload: {
    state: null,
    count: 0,
    files: [],
    finishedFiles: [],
  },
  uploader,
  getContextMenu,
  columns: [],
  folders: { children: [], files: [], unseenFilesCount: 0 } as Folder,
  currentFolder: { children: [], files: [], isRoot: true } as Folder,
  currentCollaboration: null,
  isFileRenameOn: false,
  isScanOn: false,
  isDownloading: false,
  isReadOnly: false,
  collaborators: {},
  minDate: moment(0).toISOString(),
  maxDate: moment(Number.MAX_SAFE_INTEGER).toISOString(),
  foldersLoaded: false,
  updateAgGrid: false,
  isFilterOpen: false,
  uploadDrawerOpen: false,
  agGridInsert: [],
  updatedData: [],
  latestFileId: null,
}

export const createNewItemState = (state, data, clear = false) => {
  const currentItems = clear ? [] : state.items

  if (!data.length && !clear) {
    return currentItems
  }

  const newMap = new Map(data.map((file) => [file.id, file]))
  const oldMap = new Map(currentItems.map((item) => [item.id, item]))
  const newIds = Array.from(newMap.keys())
  const oldIds = currentItems.map((item) => item.id)

  const idsToAdd = uniq([...oldIds, ...newIds].filter(Boolean))

  const newItemsState = []
  idsToAdd.forEach((id) => {
    const newItem = newMap.get(id)
    if (newItem) {
      newItemsState.push(newItem)
    } else {
      newItemsState.push(oldMap.get(id))
    }
  })

  return newItemsState
}

export default function reducer(state = initialState, action) {
  let newState = state

  switch (action.type) {
    case AG_GRID_UPDATED: {
      newState = {
        ...state,
        updateAgGrid: false,
        updatedData: [],
      }
      break
    }

    case SWITCH_COLLABORATION:
      newState = {
        ...state,
        pagination: { ...state.pagination, path: null, offset: 0 },
        currentCollaboration: action.collaborationId,
        isReadOnly: false,
      }
      break

    case SET_CLIENT_SUBSCRIPTION_EXPIRED:
      newState = {
        ...state,
        isReadOnly: action.isExpired,
      }
      break

    case UPDATE_COUNT: {
      const { count, totalCount } = action.meta
      newState = {
        ...state,
        pagination: { ...state.pagination, count, totalCount },
      }
      break
    }

    case SHOW_LOADER_DOCUMENTS: {
      newState = {
        ...state,
        isLoading: true,
        loadingText: action.kind,
      }
      break
    }

    case HIDE_LOADER_DOCUMENTS: {
      newState = {
        ...state,
        isLoading: false,
        loadingText: null,
      }
      break
    }

    case UPDATE_DOCUMENT_LIST_ITEM: {
      const { file } = action
      if (state.currentFolder?.isRoot || state.currentFolder?.id === file.folderId) {
        const foundFile = state.items.find((item) => item.id === file.id)
        file.folderId = file?.folderId || foundFile?.folderId
        file.folderPath = file?.folderPath || foundFile?.folderPath

        const updatedItems = foundFile
          ? state.items.map((item) => (item.id === file.id ? { ...foundFile, ...file } : item))
          : [...state.items, file]

        const tagIds = state.options.tagFilter.tags.map(({ id }) => id)
        const items = tagIds.length
          ? updatedItems.filter(({ tags }) => tags.find(({ id }) => tagIds.includes(id)))
          : updatedItems

        const { currentFolder, folders } = buildFolders(items, state)

        newState = {
          ...state,
          folders,
          tagToAdd: null,
          items,
          currentFolder,
          updatedData: [file],
          updateAgGrid: true,
        }

        const path = currentFolder.path ? `${currentFolder.path}/${currentFolder.name}` : currentFolder.name
        if (!foundFile && path === file.metaData?.path) {
          newState.pagination = {
            ...state.pagination,
            count: state.pagination.count + 1,
          }
        }
      } else {
        const { currentFolder, folders } = buildFolders(state.items, state)

        newState = {
          ...state,
          items: state.items.filter(({ id }) => id !== file.id),
          currentFolder,
          folders,
          updateAgGrid: true,
        }
      }

      break
    }

    case UPDATE_DOCUMENT_LIST_ITEMS: {
      const items = [...state.items]
      const selectedItems = [...state.selectedItems]

      action.files.forEach((file) => {
        const fileIndex = state.items.findIndex(({ id }) => id === file.id)
        if (fileIndex + 1) {
          items[fileIndex] = file
        } else {
          items.push(file)
        }
        const selectedFileIndex = state.selectedItems.findIndex(({ id }) => id === file.id)
        if (selectedFileIndex + 1) {
          selectedItems[selectedFileIndex] = file
        }
      })

      const { currentFolder, folders } = buildFolders(items, state)

      newState = {
        ...state,
        items,
        currentFolder,
        folders,
        selectedItems,
        tagToAdd: null,
        updatedData: action.files,
      }
      break
    }

    case REMOVE_DOCUMENTS_ITEMS: {
      const { fileIdsToRemove } = action
      const items = state.items.filter((item) => fileIdsToRemove.indexOf(item.id) === -1)

      const { currentFolder, folders } = buildFolders(items, state)

      const count = state.pagination.count - fileIdsToRemove.length
      const totalCount = state.pagination.totalCount - fileIdsToRemove.length

      newState = {
        ...state,
        items,
        currentFolder,
        folders,
        isLoading: false,
        loadingText: null,
        confirmDeleteOpen: false,
        selectedItems: state.selectedItems.filter(({ id }) => !fileIdsToRemove.includes(id)),
        pagination: {
          ...state.pagination,
          count,
          totalCount,
        },
      }
      break
    }

    case SET_DOCUMENT_PAGINATION: {
      newState = {
        ...state,
        pagination: { ...state.pagination, ...action.pagination },
        shouldRefresh: true,
      }
      break
    }

    case SET_DOCUMENTS_FILTER: {
      const { filter, excludes } = action
      newState = {
        ...state,
        shouldRefresh: true,
        items: [],
        pagination: { ...state.pagination, offset: 0 },
        options: {
          ...state.options,
          filter,
          excludes,
        },
      }
      break
    }

    case SET_DOCUMENTS_TAGFILTER: {
      const { tagFilter } = action
      newState = {
        ...state,
        shouldRefresh: true,
        items: [],
        pagination: { ...state.pagination, offset: 0 },
        options: {
          ...state.options,
          tagFilter: {
            ...state.options.tagFilter,
            ...tagFilter,
          },
        },
      }
      break
    }

    case SET_LIST_DISPLAY_TYPE: {
      const { listDisplayType } = action
      newState = { ...state, listDisplayType, shouldRefresh: true, pagination: { ...state.pagination, offset: 0 } }
      break
    }

    case SET_DOCUMENT_SORT_ORDER: {
      const {
        sortOrder: { field, value },
      } = action

      newState = {
        ...state,
        items: [],
        shouldRefresh: true,
        pagination: { ...state.pagination, offset: 0, orderBy: field, sortOrder: value },
      }
      break
    }

    case SET_DOCUMENT_SEARCH_TERM: {
      const { searchTerm } = action
      newState = {
        ...state,
        items: [],
        shouldRefresh: true,
        options: {
          ...state.options,
          searchTerm,
        },
        pagination: {
          ...state.pagination,
          offset: 0,
        },
      }
      break
    }

    case REFRESH_DOCUMENTS: {
      newState = {
        ...state,
        shouldRefresh: true,
        updateAgGrid: true,
        pagination: { ...state.pagination, offset: 0 },
        items: [],
      }
      break
    }

    case STOP_REFRESHING_DOCUMENTS: {
      newState = {
        ...state,
        shouldRefresh: false,
      }
      break
    }

    case ERROR_FETCHING_DOCUMENT_LIST_ITEMS:
    case ERROR_DELETING_DOCUMENT_LIST_ITEM:
    case ERROR_ADDING_TAGS: {
      const { error } = action

      let message = lang.defaultErrorMessage
      let title

      if (!error.body.meta) {
        throw error
      }

      if (error.body.meta.externalOwnerSystem?.[0].code === NOT_ALLOWED) {
        const isDelete = action.type === ERROR_DELETING_DOCUMENT_LIST_ITEM
        message = isDelete ? lang.documents.erpDeleteError(action.count) : lang.documents.erpEditError.message
        title = lang.documents.erpEditError.title
      }

      if (error.body.meta.label?.[0].code === EXISTS) {
        message = lang.tag.existingTagErrorMessage
      }

      if (error.body.meta.systemFolder?.[0].code === errorCodes.NOT_ALLOWED) {
        message = lang.documents.folder.systemFolderNoChildren
      }

      if (error.body.meta?.['documentInvoiceDetails.bic']?.[0]?.code === errorCodes.NO_BIC) {
        message = lang.documents.errors.invalidBic
      }

      if (error.body.meta?.['documentInvoiceDetails.iban']?.[0]?.code === errorCodes.NO_IBAN) {
        message = lang.documents.errors.invalidIban
      }

      if (error.body.meta['permission-' + PERMISSION_TYPES.DELETE]) {
        message = lang.documents.folder.permissions[PERMISSION_TYPES.DELETE].document
      }

      if (error.body.meta['permission-' + PERMISSION_TYPES.MOVE]) {
        message = lang.documents.folder.permissions[PERMISSION_TYPES.MOVE]
      }

      toast.showToast({ type: 'error', message, title })

      newState = {
        ...state,
        isLoading: false,
        loadingText: null,
        shouldRefresh: false,
        error,
        confirmDeleteOpen: false,
        updateAgGrid: true,
      }
      break
    }
    case START_DOWNLOADING_ZIP: {
      newState = {
        ...state,
        isDownloading: true,
      }
      break
    }
    case SUCCESS_DOWNLOADING_ZIP: {
      newState = {
        ...state,
        isDownloading: false,
      }
      break
    }
    case ERROR_DOWNLOADING_ZIP: {
      const isTooManyFilesError = Object.keys(action.error.meta)[0] === 'fileIdsCount'
      const errorMessage = lang.documents.errors[Object.keys(action.error.meta)[0]]

      toast.showToast({
        type: 'error',
        message: errorMessage,
        autoHideDuration: isTooManyFilesError && !action?.isTaxAccountant ? 12000 : 6000,
      })

      newState = {
        ...state,
        error: action.error,
        isDownloading: false,
      }
      break
    }

    case ERROR_UPDATING_COLUMNS:
    case ERROR_HIDING_FILTERS:
    case ERROR_FETCHING_COLUMNS:
    case ERROR_SETTING_IS_FAVORITE: {
      toast.showToast({
        type: 'error',
        message: lang.documents.errors[action.type],
      })

      newState = {
        ...state,
        error: action.error,
      }
      break
    }

    case UPDATE_TAG_FROM_DOCUMENTS_ITEMS: {
      const { tagToUpdate } = action

      const items = []
      state.items.forEach((item) => {
        const tagIndex = item.tags.findIndex((tag) => tagToUpdate.id)
        if (tagIndex >= 0) {
          items.push({
            ...item,
            tags: item.tags.map((tag) => ({ ...tag, ...(tag.id === tagToUpdate.id ? tagToUpdate : null) })),
          })
        } else {
          items.push(item)
        }
      })

      const { currentFolder, folders } = buildFolders(items, state)

      newState = {
        ...state,
        items,
        currentFolder,
        folders,
        pagination: {
          ...state.pagination,
        },
      }
      break
    }

    case REMOVE_TAG_FROM_DOCUMENTS_ITEMS: {
      const { tagToDelete } = action

      const items = []
      state.items.forEach((item) => {
        const tagIndex = item.tags.findIndex((tag) => tag.id === tagToDelete.id)
        if (tagIndex >= 0) {
          items.push({
            ...item,
            tags: item.tags.filter((tag) => tag.id !== tagToDelete.id),
          })
        } else {
          items.push(item)
        }
      })

      const { currentFolder, folders } = buildFolders(items, state)

      newState = {
        ...state,
        items,
        currentFolder,
        folders,
        pagination: {
          ...state.pagination,
        },
      }
      break
    }

    case FILE_UPLOADED: {
      const { file } = action
      const tagIds = state.options.tagFilter.tags.map(({ id }) => id)
      const updatedItems = [...state.items, file]

      const items = tagIds.length
        ? updatedItems.filter(({ tags }) => tags.find(({ id }) => tagIds.includes(id)))
        : updatedItems
      const { currentFolder, folders } = buildFolders(items, state)

      const path = currentFolder.path ? `${currentFolder.path}/${currentFolder.name}` : currentFolder.name

      const pagination = { ...state.pagination, totalCount: state.pagination.totalCount + 1 }
      if (!path || path === file.metaData?.path) {
        pagination.count++
      }

      newState = {
        ...state,
        items,
        currentFolder,
        folders,
        pagination,
        upload: { ...state.upload, finishedFiles: [...state.upload.finishedFiles, file] },
        meta: { count: pagination.count, totalCount: pagination.totalCount },
        latestFileId: file.id,
      }
      break
    }

    case FOLDER_ADDED: {
      const { currentFolder, folders } = buildFolders(state.items, { ...state, folders: action.folders })

      newState = {
        ...state,
        currentFolder,
        folders,
      }
      break
    }

    case FETCH_COMPLETE: {
      const { totalCount, data = [], minDate, maxDate, clear, fromSocket, offset, folderId, folderTotalCount } = action
      const items = createNewItemState(state, data, clear)

      const count = fromSocket ? state.pagination.count + 1 : action.count

      const { currentFolder, folders } = buildFolders(items, { ...state })

      if (currentFolder?.id === folderId && folderTotalCount !== undefined) {
        currentFolder.count = folderTotalCount
      }

      newState = {
        ...state,
        items,
        pagination: { ...state.pagination, count, totalCount, offset },
        meta: { count, totalCount },
        minDate,
        maxDate,
        isLoading: false,
        loadingText: null,
        shouldRefresh: false,
        updateAgGrid: false,
        agGridInsert: [],
        currentFolder,
        folders,
      }

      break
    }

    case OPEN_TAG_MODAL: {
      const userTagOptions = state.tagOptions.filter(({ isSystem }) => !isSystem)

      const updatedTagOptions = userTagOptions.map(({ id, label, backgroundColor }) =>
        createTagItem(
          action.file,
          action.selectedItems,
          label,
          backgroundColor,
          id,
          action.isSelected,
        ),
      )

      newState = {
        ...state,
        tagModalOpen: true,
        tagOptions: updatedTagOptions,
      }
      break
    }

    case CLOSE_TAG_MODAL: {
      newState = {
        ...state,
        tagModalOpen: false,
      }
      break
    }

    case INIT_DELETION: {
      newState = { ...state, confirmDeleteOpen: true, filesToDelete: action.files }
      break
    }

    case CANCEL_DELETION: {
      newState = { ...state, confirmDeleteOpen: false, filesToDelete: [] }
      break
    }

    case TAGS_FETCHED: {
      newState = { ...state, tagOptions: action.options }
      break
    }

    case ADD_TAG: {
      const tag = state.tagOptions.filter(({ label }) => action.label.includes(label))
      if (action.files.length === 1) {
        const existing = action.files[0].tags.find(({ label }) => label === action.label)
        if (!tag || existing) return state
      }

      newState = { ...state, tagToAdd: { tag, files: action.files } }
      break
    }

    case REMOVE_TAG: {
      const tag = state.tagOptions.filter(({ label }) => action.label.includes(label))
      if (action.files.length === 1) {
        const existing = action.files[0].tags.find(({ label }) => label === action.label)
        if (!tag || existing) return state
      }

      newState = { ...state, tagToRemove: { tag, files: action.files } }
      break
    }

    case SET_ITEM_SELECTION: {
      if (action.modifiers.shift) {
        const prevIndex = state.items.findIndex((item) => item.id === state.lastSelectedItem)
        const currentIndex = state.items.findIndex((item) => item.id === action.item.id)

        if (prevIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(prevIndex, currentIndex)
          const end = Math.max(prevIndex, currentIndex)

          newState = {
            ...state,
            selectedItems: state.items.slice(start, end + 1),
          }
        } else {
          newState = {
            ...state,
            selectedItems: action.selected
              ? [...state.selectedItems, action.item]
              : state.selectedItems.filter(({ id }) => id !== action.item.id),
            lastSelectedItem: action.item.id,
          }
        }
      } else {
        newState = {
          ...state,
          selectedItems: action.selected
            ? [...state.selectedItems, action.item]
            : state.selectedItems.filter(({ id }) => id !== action.item.id),
          lastSelectedItem: action.item.id,
        }
      }

      break
    }

    case REVOKE_SELECTION: {
      newState = {
        ...state,
        selectedItems: [],
      }
      break
    }

    case SELECT_ALL: {
      newState = {
        ...state,
        selectedItems: state.items,
      }
      break
    }

    case TOGGLE_SETTINGS_MODAL: {
      newState = { ...state, settingsModalOpen: action.open }
      break
    }

    case LIST_SETTINGS_UPDATED: {
      const updatedColumns = action.columns.map((updatedColumn) => {
        const column = state.columns.find(({ name }) => name === updatedColumn.name)

        if (!column) {
          return { ...updatedColumn, ...getColumnProps(updatedColumn.name) }
        }

        Object.keys(updatedColumn).forEach((key) => {
          column[key] = updatedColumn[key]
        })

        return column
      })

      newState = {
        ...state,
        columns: [selectColumn, ...updatedColumns].sort(({ orderId: a }, { orderId: b }) => a - b),
        settingsModalOpen: false,
      }
      break
    }

    case CLEAR_LIST: {
      newState = {
        ...initialState,
        collaborators: state.collaborators,
      }
      break
    }

    case FILTER_SETTINGS_UPDATED: {
      const { isFilterOpen } = action.isFilterOpen

      newState = {
        ...state,
        isFilterOpen,
      }
      break
    }

    case START_UPLOAD: {
      newState = {
        ...state,
        uploadDrawerOpen: true,
        isLoading: true,
        loadingText: 'processing',
        upload: { state: 'started', count: action.count, files: [], finishedFiles: [] },
      }
      break
    }

    case ADD_FILE_TO_UPLOAD: {
      newState = {
        ...state,
        upload: { ...state.upload, files: [...state.upload.files, action.file] },
      }
      break
    }

    case EXECUTE_UPLOAD: {
      newState = {
        ...state,
        upload: { ...state.upload, state: 'uploading' },
        uploadDrawerOpen: false,
        isLoading: true,
        loadingText: 'uploading',
      }
      break
    }

    case UPLOAD_COMPLETE: {
      const { uploader } = state

      uploader.methods.reset()

      newState = {
        ...state,
        uploadDrawerOpen: false,
        upload: {
          state: null,
          count: 0,
          files: [],
          finishedFiles: [],
        },
        uploaderErrors: [],
        isLoading: false,
        loadingText: null,
      }
      break
    }

    case CLOSE_UPLOAD_DRAWER: {
      state.uploader.methods.reset()

      newState = {
        ...state,
        uploadDrawerOpen: false,
        upload: {
          state: null,
          count: 0,
          files: [],
          finishedFiles: [],
        },
        isLoading: false,
        loadingText: null,
      }
      break
    }

    case REMOVE_UPLOAD_FILE: {
      const { i } = action
      state.uploader.methods.cancel(i)
      const files = [...state.upload.files]
      files.splice(i, 1)
      newState = {
        ...state,
        upload: {
          ...state.upload,
          files,
          count: state.upload.count - 1,
        },
      }
      break
    }

    case REMOVE_UPLOAD_FROM_LIST: {
      newState = {
        ...state,
        items: state.items.filter(({ uploadToken }) => uploadToken !== action.uploadToken),
        updateAgGrid: true,
      }
      break
    }

    case INSERT_DATA: {
      const { data } = action

      if (state.items.find(({ id }) => id === data.id)) {
        newState = { ...state, updateAgGrid: true }
        break
      }

      newState = {
        ...state,
        items: [data, ...state.items],
        updateAgGrid: true,
      }
      break
    }

    case SET_THUMBNAIL: {
      if (!action.fileId || !action.url) {
        break
      }

      const updatedItems = updateItemWithThumbnail(state.items, action.fileId, action.url)
      const { currentFolder, folders } = buildFolders(updatedItems, state)

      newState = {
        ...state,
        currentFolder,
        folders,
        items: updatedItems,
      }
      break
    }

    // FLAG:useDocuAi — surgical single-row patch; only dispatched on the useDocuAi path
    // (e.g. refreshDocumentListItem on FILE:ANALYSED). flag-OFF never dispatches this, so
    // the document list behaves exactly as development.
    case PATCH_DOCUMENT_LIST_ITEM: {
      const items = patchItemById(state.items, action.fileId, action.patch)
      const selectedItems = patchItemById(state.selectedItems, action.fileId, action.patch)

      if (items === state.items && selectedItems === state.selectedItems) {
        break
      }

      newState = {
        ...state,
        items,
        selectedItems,
        updatedData: [items.find(({ id }) => id === action.fileId) || selectedItems.find(({ id }) => id === action.fileId)],
        updateAgGrid: true,
      }
      break
    }

    // FLAG:useDocuAi — merge a metaData patch into one row WITHOUT replacing the whole metaData
    // (used to set/clear analysisInProgress straight from the FILE:ANALYSED payload). Synchronous,
    // so the blinking AI badge shows the instant analysis starts — it does not depend on the async
    // /meta refetch, which loses the race against the terminal review data on fast analyses.
    case PATCH_DOCUMENT_LIST_ITEM_META: {
      const items = patchItemMetaById(state.items, action.fileId, action.metaPatch)
      const selectedItems = patchItemMetaById(state.selectedItems, action.fileId, action.metaPatch)

      if (items === state.items && selectedItems === state.selectedItems) {
        break
      }

      newState = {
        ...state,
        items,
        selectedItems,
        updatedData: [items.find(({ id }) => id === action.fileId) || selectedItems.find(({ id }) => id === action.fileId)],
        updateAgGrid: true,
      }
      break
    }

    case DOCUMENT_SHARED: {
      const { fileId, isShared } = action
      const foundFile = state.items.find((item) => item.id === fileId)
      foundFile.isShared = isShared

      newState = {
        ...state,
      }
      break
    }

    case SET_FOLDER: {
      const { folder, keepSearchTerm } = action

      const path = folder.path ? `${folder.path}/${folder.name}` : folder.name
      newState = {
        ...state,
        items: [],
        options: keepSearchTerm ? { ...state.options } : { ...state.options, searchTerm: '' },
        currentFolder: { ...folder },
        pagination: { ...state.pagination, offset: 0, path },
        shouldRefresh: true,
        foldersLoaded: true,
      }

      break
    }

    case DOCUMENT_FOLDER_TREE: {
      const { currentFolder, folders } = buildFolders(state.items, { ...state, folders: action.folders })

      newState = {
        ...state,
        currentFolder,
        folders,
      }
      break
    }

    case SET_COLLABORATORS_DATA: {
      newState = {
        ...state,
        collaborators: {
          ...state.collaborators,
          [action.id]: action.hasUnseenFiles,
        },
      }
      break
    }
  }

  switch (newState.options.filter) {
    case FILE_LIST_FILTER.FAVORITE: {
      newState.items = (newState.items ?? []).filter(({ isFavorite }) => isFavorite)
      newState.currentFolder.files = (newState.currentFolder?.files ?? []).filter(({ isFavorite }) => isFavorite)
      break
    }
  }

  return newState
}

export const fetchComplete = (action) => ({
  type: FETCH_COMPLETE,
  ...action,
})

export const setTags = (options) => ({
  type: TAGS_FETCHED,
  options,
})

export const addTags = (label, files) => ({
  type: ADD_TAG,
  label,
  files,
})

export const removeTags = (label, files) => ({
  type: REMOVE_TAG,
  label,
  files,
})

export const switchCollaboration = (collaborationId) => {
  const { currentCollaboration } = store.getState().document.list

  return (dispatch) => {
    if (currentCollaboration !== collaborationId) {
      dispatch(clearList())
      dispatch({
        type: SWITCH_COLLABORATION,
        collaborationId,
      })
      dispatch(fetchListAction({ queryType: QUERY_TYPES.ALL }))
      dispatch(fetchTags())
      dispatch(fetchFolders())
      dispatch(fetchListSetting())
    }
  }
}

export const showLoader = (kind = 'fetching') => {
  return {
    type: SHOW_LOADER_DOCUMENTS,
    kind,
  }
}

export const hideLoader = () => {
  return {
    type: HIDE_LOADER_DOCUMENTS,
  }
}

export const updateDocumentListItem = (file) => {
  return {
    type: UPDATE_DOCUMENT_LIST_ITEM,
    file,
  }
}

export const refreshList = () => {
  return {
    type: REFRESH_DOCUMENTS,
  }
}

export const stopRefreshingList = () => {
  return {
    type: STOP_REFRESHING_DOCUMENTS,
  }
}

export const setPagination = (pagination) => {
  return {
    type: SET_DOCUMENT_PAGINATION,
    pagination,
  }
}

export const setSearchTerm = (searchTerm) => {
  return (dispatch) => {
    dispatch({
      type: SET_DOCUMENT_SEARCH_TERM,
      searchTerm,
    })
  }
}

export const setSortOrder = (sortOrder) => {
  return (dispatch) => {
    dispatch({
      type: SET_DOCUMENT_SORT_ORDER,
      sortOrder,
    })
  }
}

export const setActiveTagFilter = (tagFilter) => {
  return {
    type: SET_DOCUMENTS_TAGFILTER,
    tagFilter,
  }
}

export const setActiveFilter = ({ filter, excludes }) => {
  store.dispatch(updateQuickFilter(DOCUMENTS_LIST_TYPE, filter))
  return {
    type: SET_DOCUMENTS_FILTER,
    filter,
    excludes,
  }
}

export const errorFetchingDocumentListItems = (error) => {
  return {
    type: ERROR_FETCHING_DOCUMENT_LIST_ITEMS,
    error,
  }
}

export const errorDeletingDocumentListItem = (error, count = 1) => {
  return {
    type: ERROR_DELETING_DOCUMENT_LIST_ITEM,
    count,
    error,
  }
}

export const removeFiles = (fileIdsToRemove) => {
  return {
    type: REMOVE_DOCUMENTS_ITEMS,
    fileIdsToRemove,
  }
}

export const setListDisplayType = (listDisplayType) => {
  return {
    type: SET_LIST_DISPLAY_TYPE,
    listDisplayType,
  }
}

export const fetchFiles = (paginationOptions, opts = {}, cb = null) => {
  return async(dispatch) => {
    dispatch(showLoader())
    const { filter } = getList(DOCUMENTS_LIST_TYPE)
    const state = store.getState().document.list
    const { selectedItems, selectedFirst, favoritesFirst, currentFolder } = state

    const { fromSocket = false, ...additionalOptions } = opts

    const pagination = { ...state.pagination, ...paginationOptions }
    const options = { ...state.options, ...additionalOptions, favoritesFirst }

    const tagFilter = state.options.tagFilter
    const useFolders = state.options.useFolders && !(tagFilter.date.length || tagFilter.tags.length) && !currentFolder.isRoot
    options.useFolders = useFolders

    const includes = options.includes ?? []
    options.includes = selectedFirst
      ? [...includes, selectedItems.map(({ id }) => id)]
      : includes ?? []

    options.filter = filter || 'all'
    fileService.fetchFileList(pagination, options).then(async({ meta, data, folders }) => {
      const { count, totalCount, minDate, maxDate, foundIn } = meta
      await dispatch(fetchComplete({ count, totalCount, data, minDate, maxDate, folders, clear: paginationOptions?.clear, foundIn, fromSocket, meta }))
      const files = [...data]
      store.dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER }))
      cb?.({ data: files, meta: { ...meta, count } })
    }).catch((error) => dispatch(errorFetchingDocumentListItems(error)))
  }
}

export const updateFileInfo = (file, originalFile, callback) => {
  return (dispatch) => {
    return fileService.updateFileInfo(file)
      .then(async({ body: { data } }) => {
        callback?.()
        const state = store.getState().document.list
        const { listDisplayType } = state
        if (listDisplayType === FILE_LIST_TYPE.list) {
          dispatch(setShallRefresh(LIST_TYPES.DOCUMENTS_LIST_TYPE))
        }
        return dispatch(updateDocumentListItem({ ...data, thumbnailUrl: file.thumbnailUrl, metaData: { ...data.metaData } }))
      })
      .catch((error) => {
        dispatch(errorFetchingDocumentListItems(error))
        throw error
      })
  }
}

export const moveFiles = (fileIds, folderId) => {
  return (dispatch, getState) => {
    dispatch(showLoader('movingFiles'))
    return fileService.moveFiles(fileIds, folderId)
      .then(async() => {
        const { listDisplayType } = getState().document.list

        if (listDisplayType === FILE_LIST_TYPE.thumbs) {
          return dispatch(refreshList())
        }
        if (listDisplayType === FILE_LIST_TYPE.list) {
          dispatch(setShallRefresh(LIST_TYPES.DOCUMENTS_LIST_TYPE))
          dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER }))
          dispatch(fetchFolders())
          dispatch(revokeSelection())
          dispatch(hideLoader())
        }
      })
      .catch((error) => {
        dispatch(errorFetchingDocumentListItems(error))
      })
  }
}

export const checkDuplicateFile = (fileIds, folderId) => {
  return () => {
    return fileService.checkDuplicate(fileIds, folderId)
      .then(async(res) => {
        const { body: { data: existingFileIds } } = res
        return existingFileIds
      })
  }
}

export const updateTagOnFiles = (tagToUpdate) => {
  return {
    type: UPDATE_TAG_FROM_DOCUMENTS_ITEMS,
    tagToUpdate,
  }
}

export const deleteTagFromFiles = (tagToDelete) => {
  return {
    type: REMOVE_TAG_FROM_DOCUMENTS_ITEMS,
    tagToDelete,
  }
}

export const EDIT_DRAWER_TYPES = {
  EDIT: {
    isFileRenameOn: false,
    isScanOn: false,
  },
  RENAME: {
    isFileRenameOn: true,
    isScanOn: false,
  },
  SCAN: {
    isFileRenameOn: false,
    isScanOn: true,
  },
}

export const openTagModal = (file, selectedItems, isSelected) => ({
  type: OPEN_TAG_MODAL,
  file,
  selectedItems,
  isSelected,
})

export const closeTagModal = () => ({
  type: CLOSE_TAG_MODAL,
})

export const initDeletion = (files) => {
  return {
    type: INIT_DELETION,
    files,
  }
}

export const cancelDeletion = () => {
  return {
    type: CANCEL_DELETION,
  }
}

export const setItemSelection = (item, selected, modifiers) => ({
  type: SET_ITEM_SELECTION,
  selected,
  item,
  modifiers,
})

export const revokeSelection = () => ({
  type: REVOKE_SELECTION,
})

export const selectAll = () => ({
  type: SELECT_ALL,
})

export const agGridUpdated = () => ({
  type: AG_GRID_UPDATED,
})

export const updateDocumentListItems = (files) => ({
  type: UPDATE_DOCUMENT_LIST_ITEMS,
  files,
})

export const toggleSettingModal = (open) => ({
  type: TOGGLE_SETTINGS_MODAL,
  open,
})

export const fetchFilterSetting = () => {
  return (dispatch) => {
    request(documentConfig.listSetting, {
      auth: true,
      isConcurrent: true,
      method: 'GET',
    })
      .then((response) => {
        if (response?.body?.meta) {
          dispatch({
            type: FILTER_SETTINGS_UPDATED,
            isFilterOpen: response.body.meta,
          })
        }
      })
      .catch((error) => {
        dispatch({ type: ERROR_HIDING_FILTERS, error })
      })
  }
}

export const updateFilterSetting = (isFilterOpen) => {
  return (dispatch) => {
    const sendRequest = request(`${documentConfig.listSetting}/meta`, {
      auth: true,
      method: 'PUT',
      data: { isFilterOpen },
    })
    sendRequest
      .then((response) => {
        if (response?.body?.data) {
          dispatch({
            type: FILTER_SETTINGS_UPDATED,
            isFilterOpen: response.body.data,
          })
        }
      })
      .catch((error) => {
        dispatch({ type: ERROR_HIDING_FILTERS, error })
      })
  }
}

export const updateColumnSettings = (data) => {
  return (dispatch) => {
    const sendRequest = request(`${documentConfig.listSetting}/columns`, {
      auth: true,
      method: 'PUT',
      data: data
        .filter((item) => item.name && item.name !== 'select')
        .map((column) => pick(column, ['name', 'label', 'active', 'width', 'required'])),
    })

    sendRequest
      .then((response) => {
        if (response?.body?.data) {
          dispatch({
            type: LIST_SETTINGS_UPDATED,
            columns: response.body.data,
          })
        }
      })
      .catch((error) => {
        dispatch({ type: ERROR_UPDATING_COLUMNS, error })
      })
  }
}

export const clearList = () => ({
  type: CLEAR_LIST,
})

export const setThumbnail = (fileId, url) => ({
  type: SET_THUMBNAIL,
  fileId,
  url,
})

// FLAG:useDocuAi — surgically patch a single list row (used by the useDocuAi refresh path).
export const patchDocumentListItem = (fileId, patch) => ({
  type: PATCH_DOCUMENT_LIST_ITEM,
  fileId,
  patch,
})

// FLAG:useDocuAi — merge a metaData patch into one row (e.g. { analysisInProgress: true })
// straight from the FILE:ANALYSED payload, without an async /meta refetch.
export const patchDocumentListItemMeta = (fileId, metaPatch) => ({
  type: PATCH_DOCUMENT_LIST_ITEM_META,
  fileId,
  metaPatch,
})

export const updateFolderTree = (folders) => {
  return {
    type: DOCUMENT_FOLDER_TREE,
    folders,
  }
}

export const setCollaboratorsData = (id, hasUnseenFiles) => {
  return {
    type: SET_COLLABORATORS_DATA,
    id,
    hasUnseenFiles,
  }
}

export const setFolder = async(folder, { keepSearchTerm = false } = {}) => {
  store.dispatch({
    type: SET_FOLDER,
    folder,
    keepSearchTerm,
  })
}

export const updateSharedState = (fileId, isShared) =>
  store.dispatch({
    type: DOCUMENT_SHARED,
    isShared,
    fileId,
  })

export const updateFolders = (folders) =>
  store.dispatch({
    type: FOLDER_ADDED,
    folders,
  })

export const executeUpload = () => ({
  type: EXECUTE_UPLOAD,
})

export const markAsSeen = (files, currentFolder) => {
  return async(dispatch) => {
    dispatch(showLoader('processing'))

    const fileIds = files
      .filter((file) => file.unseen)
      .map((file) => file.id)

    const path = currentFolder.path
      ? `${currentFolder.path}/${currentFolder.name}`
      : currentFolder.name

    try {
      await fileService.markAsSeen(path, fileIds)
      dispatch(hideLoader())
    } catch (error) {
      toast.showToast({
        type: 'error',
        message: lang.documents.folder.markAsSeenFailed,
      })
      dispatch(hideLoader())
    }
  }
}

const insertData = (data) => ({
  type: INSERT_DATA,
  data,
})

export const applyFilter = (file) => {
  const { currentFolder, options } = getAppState().document.list
  const { searchTerm, tagFilter } = options
  const { tags = [], date = [] } = tagFilter
  const { quickFilter, filterModel: filters = {} } = getList(DOCUMENTS_LIST_TYPE)
  const dateFilter = buildDateFilter(date).recordDate
  const filterModel = { ...filters, ...quickFilter }

  let match = true
  const setMatch = (value) => {
    match = value
    if (!match && filterModel.operator?.toLowercase() !== 'or') throw new Error()
  }

  const filterKeys = Object.keys(filterModel)
  for (const key of filterKeys) {
    try {
      const value = file[key]
      const { type, filterType, values, filter } = filterModel[key]

      if (type === 'inRange' && filterType === 'date') {
        const date = moment(value)
        setMatch(!(date.isBefore(filterModel.dateFrom) || date.isAfter(filterModel.dateTo)))
      } else if (type === 'set' && values?.length) {
        setMatch(values.includes(value))
      } else if (type === 'equals' && filterType === 'text') {
        setMatch(value === filter)
      } else if (type === 'notNull') {
        setMatch(value !== null && value !== undefined)
      }
    } catch (_e) {
      break
    }
  }
  if (!match) return false

  const recordDate = dateFilter ? moment(file.recordDate) : null
  if (recordDate && (recordDate.isBefore(dateFilter.dateFrom) || recordDate.isAfter(dateFilter.dateTo))) return false
  if (currentFolder.id && file.folderId !== currentFolder.id) return false
  if (tags.length && !tags.some(({ id }) => file.tags.map(({ id }) => id).includes(id))) return false
  if (searchTerm && !file.decodedName?.includes(searchTerm)) return false

  return true
}

export const onNewFile = (msg) => {
  const state = store.getState()
  const { upload } = state.document.list
  const { processing } = state.document.upload
  if (upload?.state === 'uploading' && upload.files.length > 1) {
    return
  }
  if (msg.data?.file) {
    if (!processing) {
      debounce(() => store.dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER })), 500)()
    }
    if (applyFilter(msg.data.file)) {
      store.dispatch(insertData(msg.data.file))
    }
  } else if (msg.data?.fileId) {
    if (msg.data?.folderName && msg.data?.folderName === state.document.list.currentFolder?.folderPath) {
      if (!processing) {
        store.dispatch(fetchListAction({ params: { includes: [msg.data.fileId] }, queryType: QUERY_TYPES.ALL }))
      }
    } else {
      if (!processing) {
        dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER }))
      }
    }
    if (!processing) {
      store.dispatch(fetchFolders())
    }
  }
}

export const removeUploadFile = (i) => {
  store.dispatch({
    type: REMOVE_UPLOAD_FILE,
    i,
  })
}

export const closeUploadDrawer = () => ({
  type: CLOSE_UPLOAD_DRAWER,
})

export const removeUploadFromList = (uploadToken) => ({
  type: REMOVE_UPLOAD_FROM_LIST,
  uploadToken,
})

export const setReadOnly = (isExpired: boolean) => ({
  type: SET_CLIENT_SUBSCRIPTION_EXPIRED,
  isExpired,
})

// Helper functions
const updateItemWithThumbnail = (items, fileId, thumbnailUrl) => {
  const itemIndex = items.findIndex(({ id }) => id === fileId)
  if (itemIndex === -1 || !thumbnailUrl) return items

  const updatedItems = [...items]
  updatedItems[itemIndex] = { ...updatedItems[itemIndex], thumbnailUrl }
  return updatedItems
}
